import { type AfterViewInit, Component, Input, ViewChild, inject } from '@angular/core';
import { ToastUiImageEditorComponent, ToastUiImageEditorModule } from '@moddi3/ngx-tui-image-editor';
import type { SelectedFileExtended } from '@common/types/file.type';
import { tuiImageEditorTheme } from '@components/sp-image-editor/image-editor.config';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'sp-image-editor',
    templateUrl: './sp-image-editor.component.html',
    styleUrls: ['./sp-image-editor.component.less'],
    exportAs: 'image-editor',
    imports: [ToastUiImageEditorModule],
})
export class SpImageEditorComponent implements AfterViewInit {
    private readonly translate = inject(TranslateService);

    @Input() image: SelectedFileExtended;
    @ViewChild(ToastUiImageEditorComponent) editorComponent;
    tuiImageEditorLocale = {
        Apply: `${this.translate.instant('tui_apply')}`,
        Arrow: `${this.translate.instant('tui_arrow')}`,
        'Arrow-2': `${this.translate.instant('tui_arrow2')}`,
        'Arrow-3': `${this.translate.instant('tui_arrow3')}`,
        Blend: `${this.translate.instant('tui_blend')}`,
        Blur: `${this.translate.instant('tui_blur')}`,
        Bold: `${this.translate.instant('tui_bold')}`,
        Brightness: `${this.translate.instant('tui_brightness')}`,
        Bubble: `${this.translate.instant('tui_bubble')}`,
        Cancel: `${this.translate.instant('tui_cancel')}`,
        Center: `${this.translate.instant('tui_center')}`,
        Circle: `${this.translate.instant('tui_circle')}`,
        Color: `${this.translate.instant('tui_color')}`,
        'Color Filter': `${this.translate.instant('tui_color_filter')}`,
        Crop: `${this.translate.instant('tui_crop')}`,
        Custom: `${this.translate.instant('tui_custom')}`,
        'Custom icon': `${this.translate.instant('tui_custom_icon')}`,
        Delete: `${this.translate.instant('tui_delete')}`,
        DeleteAll: `${this.translate.instant('tui_delete_all')}`,
        Distance: `${this.translate.instant('tui_distance')}`,
        Download: `${this.translate.instant('tui_download')}`,
        Draw: `${this.translate.instant('tui_draw')}`,
        Emboss: `${this.translate.instant('tui_emboss')}`,
        Fill: `${this.translate.instant('tui_fill')}`,
        Filter: `${this.translate.instant('tui_filter')}`,
        Flip: `${this.translate.instant('tui_flip')}`,
        'Flip X': `${this.translate.instant('tui_flip_x')}`,
        'Flip Y': `${this.translate.instant('tui_flip_y')}`,
        Free: `${this.translate.instant('tui_free')}`,
        Grayscale: `${this.translate.instant('tui_grayscale')}`,
        Hand: `${this.translate.instant('tui_hand')}`,
        Heart: `${this.translate.instant('tui_heart')}`,
        History: `${this.translate.instant('tui_history')}`,
        Icon: `${this.translate.instant('tui_icon')}`,
        Invert: `${this.translate.instant('tui_invert')}`,
        Italic: `${this.translate.instant('tui_italic')}`,
        Left: `${this.translate.instant('tui_left')}`,
        Load: `${this.translate.instant('tui_load')}`,
        'Load Mask Image': `${this.translate.instant('tui_load_mask_image')}`,
        'Lock Aspect Ratio': `${this.translate.instant('tui_lock_aspect_ratio')}`,
        Location: `${this.translate.instant('tui_location')}`,
        Mask: `${this.translate.instant('tui_mask')}`,
        Multiply: `${this.translate.instant('tui_multiply')}`,
        Noise: `${this.translate.instant('tui_noise')}`,
        Pixelate: `${this.translate.instant('tui_pixelate')}`,
        Polygon: `${this.translate.instant('tui_polygon')}`,
        Range: `${this.translate.instant('tui_range')}`,
        Rectangle: `${this.translate.instant('tui_rectangle')}`,
        Redo: `${this.translate.instant('tui_redo')}`,
        'Remove White': `${this.translate.instant('tui_remove_white')}`,
        Reset: `${this.translate.instant('tui_reset')}`,
        Resize: `${this.translate.instant('tui_resize')}`,
        Right: `${this.translate.instant('tui_right')}`,
        Rotate: `${this.translate.instant('tui_rotate')}`,
        Sepia: `${this.translate.instant('tui_sepia')}`,
        Sepia2: `${this.translate.instant('tui_sepia2')}`,
        Shape: `${this.translate.instant('tui_shape')}`,
        Sharpen: `${this.translate.instant('tui_sharpen')}`,
        Square: `${this.translate.instant('tui_square')}`,
        'Star-1': `${this.translate.instant('tui_star1')}`,
        'Star-2': `${this.translate.instant('tui_star2')}`,
        Straight: `${this.translate.instant('tui_straight')}`,
        Stroke: `${this.translate.instant('tui_stroke')}`,
        Text: `${this.translate.instant('tui_text')}`,
        'Text size': `${this.translate.instant('tui_text_size')}`,
        Threshold: `${this.translate.instant('tui_threshold')}`,
        Tint: `${this.translate.instant('tui_tint')}`,
        Triangle: `${this.translate.instant('tui_triangle')}`,
        Underline: `${this.translate.instant('tui_underline')}`,
        Undo: `${this.translate.instant('tui_undo')}`,
        Value: `${this.translate.instant('tui_value')}`,
        ZoomIn: `${this.translate.instant('tui_zoom_in')}`,
        ZoomOut: `${this.translate.instant('tui_zoom_out')}`,
        Width: `${this.translate.instant('tui_image_width')}`,
        Height: `${this.translate.instant('tui_image_height')}`,
    };
    options = {
        usageStatistics: false,
        includeUI: {
            menuBarPosition: 'left',
            menu: ['crop', 'flip', 'rotate', 'draw', 'shape', 'icon', 'text', 'filter'],
            uiSize: {
                width: '100%',
                height: '500px',
            },
            initMenu: '',
            theme: tuiImageEditorTheme,
            locale: this.tuiImageEditorLocale,
        },
    };

    ngAfterViewInit() {
        this.editorComponent.editorInstance
            .loadImageFromURL(
                `${
                    this.image.sendpulseCdn ? this.image.sendpulseCdn : this.generatedSendpulseCdn
                }${this.image.url.substring(1)}`,
                this.image.name
            )
            .then(() => this.editorComponent.editorInstance.ui?.activeMenuEvent());
    }

    get generatedSendpulseCdn(): string {
        const urlParts = this.image.url.split('/');
        const userId = urlParts[1].substring(32);

        return `https://s${userId}.sendpul.se/image/files/emailservice/userfiles/`;
    }
}
